import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import AgencyMap from './AgencyMap';
import { getTrips } from '../../services/tripsService';
import { getVehicles } from '../../services/vehiclesService';
import { getDrivers } from '../../services/driversService';
import { getExpiringDriverDocuments } from '../../services/driverDocumentsService';

const DashboardPage = () => {
  const [totalTripsToday, setTotalTripsToday] = useState(0);
  const [vehiclesAvailable, setVehiclesAvailable] = useState(0);
  const [driversOnTrip, setDriversOnTrip] = useState(0);
  const [expiringDocuments, setExpiringDocuments] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Get today's date range (start and end of today)
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        // Fetch all metrics in parallel
        const [tripsResponse, allVehiclesResponse, driversResponse, expiringDocsResponse, activeTripsResponse] = await Promise.all([
          // 1. Total Trips Today
          getTrips({
            start_date: startOfToday.toISOString(),
            end_date: endOfToday.toISOString(),
            limit: 100, // API max limit is 100
            offset: 0
          }),
          // 2. All Vehicles (to calculate available ones)
          getVehicles({
            page: 1,
            limit: 100 // API max limit is 100
          }),
          // 3. Drivers - we'll filter for drivers on active trips
          getDrivers({
            page: 1,
            limit: 100 // API max limit is 100
          }),
          // 4. Expiring Documents
          getExpiringDriverDocuments(),
          // 5. Active Trips (for calculating vehicles available and drivers on trip)
          getTrips({
            trip_status: 'In Progress',
            limit: 100, // API max limit is 100
            offset: 0
          })
        ]);

        // Process Total Trips Today
        let tripsCount = 0;
        if (tripsResponse?.data) {
          if (Array.isArray(tripsResponse.data)) {
            tripsCount = tripsResponse.data.length;
          } else if (tripsResponse.data.items) {
            tripsCount = tripsResponse.data.items.length;
          } else if (tripsResponse.data.total !== undefined) {
            tripsCount = tripsResponse.data.total;
          }
        } else if (Array.isArray(tripsResponse)) {
          tripsCount = tripsResponse.length;
        } else if (tripsResponse?.items) {
          tripsCount = tripsResponse.items.length;
        } else if (tripsResponse?.total !== undefined) {
          tripsCount = tripsResponse.total;
        }
        setTotalTripsToday(tripsCount);

        // Process Vehicles Available
        // Get all vehicles to calculate available ones
        let allVehicles = [];
        if (allVehiclesResponse?.data) {
          if (Array.isArray(allVehiclesResponse.data)) {
            allVehicles = allVehiclesResponse.data;
          } else if (allVehiclesResponse.data.items) {
            allVehicles = allVehiclesResponse.data.items;
          }
        } else if (Array.isArray(allVehiclesResponse)) {
          allVehicles = allVehiclesResponse;
        } else if (allVehiclesResponse?.items) {
          allVehicles = allVehiclesResponse.items;
        }

        // Process active trips for both vehicles and drivers
        let activeTrips = [];
        if (activeTripsResponse?.data) {
          if (Array.isArray(activeTripsResponse.data)) {
            activeTrips = activeTripsResponse.data;
          } else if (activeTripsResponse.data.items) {
            activeTrips = activeTripsResponse.data.items;
          }
        } else if (Array.isArray(activeTripsResponse)) {
          activeTrips = activeTripsResponse;
        } else if (activeTripsResponse?.items) {
          activeTrips = activeTripsResponse.items;
        }

        // Get vehicles currently on active trips
        const vehiclesOnActiveTrips = new Set();
        activeTrips.forEach(trip => {
          if (trip.vehicle_id) {
            vehiclesOnActiveTrips.add(trip.vehicle_id);
          } else if (trip.vehicle?.id) {
            vehiclesOnActiveTrips.add(trip.vehicle.id);
          }
        });

        // Count vehicles that are not on active trips and have status 'Active' or 'Available'
        const availableVehicles = allVehicles.filter(vehicle => {
          const vehicleId = vehicle.id || vehicle.vehicle_id;
          const isOnTrip = vehiclesOnActiveTrips.has(vehicleId);
          const status = vehicle.status || vehicle.vehicle_status || '';
          return !isOnTrip && (status === 'Active' || status === 'Available');
        });

        setVehiclesAvailable(availableVehicles.length);

        // Process Drivers On Trip

        // Count unique drivers on active trips
        const driversOnActiveTrips = new Set();
        activeTrips.forEach(trip => {
          if (trip.assigned_driver_id) {
            driversOnActiveTrips.add(trip.assigned_driver_id);
          } else if (trip.assigned_driver?.id) {
            driversOnActiveTrips.add(trip.assigned_driver.id);
          } else if (trip.driver_id) {
            driversOnActiveTrips.add(trip.driver_id);
          }
        });
        setDriversOnTrip(driversOnActiveTrips.size);

        // Process Expiring Documents
        let expiringDocsCount = 0;
        if (expiringDocsResponse) {
          if (Array.isArray(expiringDocsResponse)) {
            expiringDocsCount = expiringDocsResponse.length;
          } else if (expiringDocsResponse.data) {
            if (Array.isArray(expiringDocsResponse.data)) {
              expiringDocsCount = expiringDocsResponse.data.length;
            } else if (expiringDocsResponse.data.items) {
              expiringDocsCount = expiringDocsResponse.data.items.length;
            } else if (expiringDocsResponse.data.count !== undefined) {
              expiringDocsCount = expiringDocsResponse.data.count;
            }
          } else if (expiringDocsResponse.count !== undefined) {
            expiringDocsCount = expiringDocsResponse.count;
          }
        }
        setExpiringDocuments(expiringDocsCount);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set defaults on error
        setTotalTripsToday(0);
        setVehiclesAvailable(0);
        setDriversOnTrip(0);
        setExpiringDocuments(0);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard-content">
      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card blue">
          <div className="metric-icon">🚚</div>
          <div className="metric-content">
            <div className="metric-value">{loading ? '...' : totalTripsToday}</div>
            <div className="metric-label">Total Trips Today</div>
            <div className="metric-change positive">↑ Today</div>
          </div>
        </div>

        <div className="metric-card green">
          <div className="metric-icon">🚗</div>
          <div className="metric-content">
            <div className="metric-value">{loading ? '...' : vehiclesAvailable}</div>
            <div className="metric-label">Vehicles Available</div>
            <div className="metric-change positive">Available</div>
          </div>
        </div>

        <div className="metric-card purple">
          <div className="metric-icon">👤</div>
          <div className="metric-content">
            <div className="metric-value">{loading ? '...' : driversOnTrip}</div>
            <div className="metric-label">Drivers On Trip</div>
            <div className="metric-change positive">Active</div>
          </div>
        </div>

        <div className="metric-card red">
          <div className="metric-icon">⚠️</div>
          <div className="metric-content">
            <div className="metric-value">{loading ? '...' : expiringDocuments}</div>
            <div className="metric-label">Expiring Documents</div>
            <div className="metric-change positive">Expiring Soon</div>
          </div>
        </div>
      </div>

      {/* Transporters Map */}
      <AgencyMap />
    </div>
  );
};

export default DashboardPage;
